import { useState, useEffect } from "react";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import adistoreLogo from "@/assets/adistore-mascot.png";

const navLinks = [
{ label: "Hem", href: "#hero" },
{ label: "Automater", href: "#produkter" },
{ label: "Tjänster", href: "#tjanster" },
{ label: "Sortiment", href: "#sortiment" },
{ label: "Kontakt", href: "#kontakt" }];


const StickyHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 60);
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      // Om ankaret finns på den aktuella sidan (t.ex. kontaktformuläret på
      // stads-/modellsidor) skrollar vi dit lokalt istället för att lämna sidan.
      const localTarget = document.querySelector(href);
      if (location.pathname !== "/" && !localTarget) {
        navigate("/");
        setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ?
      "bg-[hsl(216,60%,8%)]/95 backdrop-blur-md shadow-dramatic border-b border-white/5 py-2" :
      "bg-transparent py-4"}`
      }>

      <div className="container mx-auto flex items-center justify-between px-4">
        <Link
          to="/"
          aria-label="AdiStore – till startsidan"
          className="flex items-center"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              document.querySelector("#hero")?.scrollIntoView({ behavior: "smooth" });
            }
          }}
        >
          <div className="h-28 md:h-32 overflow-hidden">
            <img src={adistoreLogo} alt="AdiStore" width={300} height={200} decoding="async" className="h-full w-auto object-contain" />
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) =>
          <button
            key={l.href}
            onClick={() => handleNavClick(l.href)}
            className="font-body rounded-lg px-3 py-2 text-[15px] font-semibold text-white hover:text-white hover:bg-white/10 transition-all duration-200">

              {l.label}
            </button>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:0706291999"
            className="flex items-center gap-2 text-[15px] font-semibold text-white hover:text-white/80 transition-colors">

            <Phone className="h-4 w-4" />
            070 629 19 99
          </a>
          <button
            onClick={() => handleNavClick("#kontakt")}
            className="group flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-[13px] font-semibold text-accent-foreground hover:bg-orange-hover transition-all duration-300 shadow-lg shadow-accent/20">

            Få en offert
            <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        <button
          aria-label={mobileOpen ? "Stäng meny" : "Öppna meny"}
          className="lg:hidden text-white p-1"
          onClick={() => setMobileOpen(!mobileOpen)}>

          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <div
        className={`lg:hidden bg-[hsl(216,60%,8%)] overflow-hidden border-t border-white/5 transition-all duration-300 ease-out ${
          mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0 border-t-0"
        }`}
      >
        <nav className="flex flex-col px-4 pb-6 pt-2 gap-0.5">
          {navLinks.map((l) =>
          <button
            key={l.href}
            onClick={() => handleNavClick(l.href)}
            className="text-left py-3 px-3 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors">

              {l.label}
            </button>
          )}
          <hr className="border-white/10 my-2" />
          <a
            href="tel:0706291999"
            className="flex items-center gap-2 py-3 px-3 text-sm font-medium text-white/70">

            <Phone className="h-4 w-4" />
            070 629 19 99
          </a>
          <button
            onClick={() => handleNavClick("#kontakt")}
            className="mt-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground">

            Få en offert
          </button>
        </nav>
      </div>
    </header>);

};

export default StickyHeader;
